import React from "react";
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, MenuList, Popover } from "@mui/material";
import { AddShoppingCart, Check, ChevronLeft, Discount, DisplaySettings, Edit, IosShare, Settings } from "@mui/icons-material";
import { ListingCellCenter } from "../../../components/commonComponents/TabularLists/styles";
import { HtmlTooltip, HtmlTooltipDisabled } from "../../../components/commonComponents/StyledComponents/styled";
import { checkUserPermissions } from "../../../api_calls/userManagement";
import { fetchRequiredDetailsBeforePopup } from "../../../api_calls/marketingActivities";
import { isBrandReportShareable } from "../../../api_calls/utils";
import { canEditAcrossCompanies, canShareCampaignByOwner } from "../../../api_calls/crossCompanyAccess";
import { tenantRetailerScopeAllowsOperation } from "../../../api_calls/tenantRetailerRecordAccess";
import { useSelector } from "react-redux";

// Same product-gate as the Performance "Add Products" button: 0 and 1 mean none added yet.
const hasAnalyticsProducts = (campaign) => {
  return ![0, 1].includes(campaign.campaign_status_code);
};

const hasMarketingActivities = (campaign) => campaign.has_marketing_activities === true;

const menuItemSx = {
  fontSize: 14,
  minWidth: 240,
  py: 1,
  cursor: "pointer",
  "&.Mui-disabled": {
    opacity: 1,
    cursor: "default",
  },
};

/**
 * One row in the Action gear menu.
 * Checkmarks are optional (analytics products, marketing activities, share).
 */
const ActionMenuRow = ({
  enabled,
  icon,
  label,
  ariaLabelText,
  showCheck,
  onClick,
  disabledTooltip,
}) => {
  const row = (
    <MenuItem
      disabled={enabled !== true}
      onClick={(event) => {
        event.preventDefault();
        if (enabled === true && typeof onClick === "function") {
          onClick(event);
        }
      }}
      aria-label={ariaLabelText}
      sx={menuItemSx}
    >
      <ListItemIcon sx={{ minWidth: 36, color: enabled === true ? "#AF3241" : "#E6E6E6" }}>
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          sx: { color: enabled === true ? "#1E1E28" : "#C8C8C8", fontSize: 14 },
        }}
      />
      {/* Empty space when the step is not done — no grey placeholder icon */}
      {showCheck === true ? <Check sx={{ color: "#AF3241", fontSize: 18 }} /> : null}
    </MenuItem>
  );

  // A disabled menu item does not receive hover, so the tooltip sits on a wrapper.
  if (enabled !== true && typeof disabledTooltip === "string" && disabledTooltip !== "") {
    return (
      <HtmlTooltipDisabled
        title={disabledTooltip}
        arrow
        placement="left"
        enterDelay={DISABLED_MENU_TOOLTIP_ENTER_DELAY_MS}
        enterNextDelay={DISABLED_MENU_TOOLTIP_ENTER_DELAY_MS}
      >
        <span style={{ display: "block" }}>{row}</span>
      </HtmlTooltipDisabled>
    );
  }

  return row;
};

const NESTED_CLOSE_DELAY_MS = 180;
const DISABLED_MENU_TOOLTIP_ENTER_DELAY_MS = 400;
const NO_ACCESS_TOOLTIP = "No access";

const CampaignActions = ({
  campaign,
  checkEditAccess,
  userInfo,
  setCampaignInfo,
  setOpenModal,
  fetchCampaign,
  onInternalSettingSelect,
}) => {
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [nestedAnchor, setNestedAnchor] = React.useState(null);
  const nestedCloseTimer = React.useRef(null);
  const nestedPaperRef = React.useRef(null);
  const internalSettingsRef = React.useRef(null);
  const gearRef = React.useRef(null);
  const userSelections = useSelector((state) => state.userSelections);

  // Owner company comes from list/detail as owner_company_* (API: company_retailer).
  // Cyreen and tenant may edit a campaign owned by another company.
  const editsAcrossCompanies = canEditAcrossCompanies(userInfo.user_type);
  // retailer_names is the same list the Retailer column shows.
  const tenantRetailerOk = tenantRetailerScopeAllowsOperation(
    userInfo.user_type,
    campaign.retailer_names,
    userSelections
  );
  const isAvailableToList =
    tenantRetailerOk === true &&
    (userInfo.current_workspace.name === campaign.owner_company_name || editsAcrossCompanies === true);
  const isEditAccessible = checkEditAccess.success === true && isAvailableToList;
  const isUserAuthorized =
    isEditAccessible &&
    typeof userInfo.current_workspace !== "undefined" &&
    tenantRetailerOk === true &&
    (userInfo.current_workspace.id === campaign.owner_company_id || editsAcrossCompanies === true);
  // Share when performance or impressions is ready (same rule as the analytics page).
  const reportCanBeShared = isBrandReportShareable(campaign);
  const canShareByOwner = canShareCampaignByOwner(userInfo.user_type, userInfo, campaign);
  const isReportShareable =
    checkEditAccess.success === true && reportCanBeShared === true && canShareByOwner === true;
  const shareDisabledTooltip =
    canShareByOwner !== true
      ? NO_ACCESS_TOOLTIP
      : reportCanBeShared !== true
        ? "A report must be available"
        : "";
  const productsAdded = hasAnalyticsProducts(campaign);
  const activitiesAdded = hasMarketingActivities(campaign);
  const campaignWasShared = campaign.shared === true;
  const campaignIsCompleted = campaign.campaign_status === "Completed";
  const productsDisabledTooltip = isEditAccessible !== true ? NO_ACCESS_TOOLTIP : "";
  const activitiesDisabledTooltip =
    isEditAccessible !== true
      ? NO_ACCESS_TOOLTIP
      : productsAdded !== true
        ? "Add analytics products first"
        : "";
  const editDisabledTooltip =
    campaignIsCompleted === true
      ? "Campaign is completed"
      : isUserAuthorized !== true
        ? NO_ACCESS_TOOLTIP
        : "";

  const showProducts = permissionScope.campaign_products_can_edit === true;
  const showActivities = permissionScope.campaign_activities_can_edit === true;
  const showShare = permissionScope.campaign_can_share === true;
  const showEdit = permissionScope.campaign_list_can_edit === true;
  const showInternalSettings = permissionScope.campaign_reports_internal_settings_can_view === true;
  const showMenuDivider =
    (showProducts === true || showActivities === true || showShare === true) &&
    (showEdit === true || showInternalSettings === true);

  const canViewReportSettings = permissionScope.campaign_reports_settings_can_view === true;
  // Internal Notes are Cyreen-only (like the store Internal Notes tab) and need Report Settings access
  const showInternalNotes = canViewReportSettings === true && userInfo.user_type === "cyreen";
  const nestedMenuOpen = Boolean(nestedAnchor);

  const cancelNestedClose = () => {
    if (nestedCloseTimer.current !== null) {
      clearTimeout(nestedCloseTimer.current);
      nestedCloseTimer.current = null;
    }
  };

  const closeNestedMenu = () => {
    cancelNestedClose();
    setNestedAnchor(null);
  };

  const closeMenu = () => {
    closeNestedMenu();
    setMenuAnchor(null);
  };

  const openMenu = (event) => {
    event.preventDefault();
    // A retailer mismatch blocks the whole menu, including Share and Internal settings.
    if (tenantRetailerOk !== true) {
      return;
    }
    setMenuAnchor(event.currentTarget);
  };

  // Keep the submenu up long enough to move the pointer from Internal settings onto it
  const scheduleNestedClose = () => {
    cancelNestedClose();
    nestedCloseTimer.current = setTimeout(() => {
      setNestedAnchor(null);
    }, NESTED_CLOSE_DELAY_MS);
  };

  const isInsideHoverZone = (relatedTarget) => {
    if (!relatedTarget || typeof relatedTarget.closest !== "function") {
      return false;
    }
    if (internalSettingsRef.current && internalSettingsRef.current.contains(relatedTarget)) {
      return true;
    }
    if (nestedPaperRef.current && nestedPaperRef.current.contains(relatedTarget)) {
      return true;
    }
    return false;
  };

  const handleLeaveHoverZone = (event) => {
    if (isInsideHoverZone(event.relatedTarget) === true) {
      cancelNestedClose();
      return;
    }
    scheduleNestedClose();
  };

  const openNestedMenu = (anchorEl) => {
    cancelNestedClose();
    // Same row already open — skip setState so hover does not loop
    setNestedAnchor((current) => (current === anchorEl ? current : anchorEl));
  };

  React.useEffect(() => {
    return () => {
      cancelNestedClose();
    };
  }, []);

  return (
    <ListingCellCenter>
      {tenantRetailerOk === true ? (
        <HtmlTooltip title="Actions">
          <Box
            ref={gearRef}
            component="a"
            href="javascript:void(0)"
            aria-label={"campaign actions for " + campaign.campaign_name}
            onClick={openMenu}
            sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
          >
            <Settings fontSize="small" sx={{ color: "#AF3241" }} />
          </Box>
        </HtmlTooltip>
      ) : (
        <HtmlTooltipDisabled title={NO_ACCESS_TOOLTIP}>
          <Box
            ref={gearRef}
            component="a"
            href="javascript:void(0)"
            aria-label={"campaign actions for " + campaign.campaign_name}
            onClick={openMenu}
            sx={{ display: "inline-flex", alignItems: "center", cursor: "default" }}
          >
            <Settings fontSize="small" sx={{ color: "#E6E6E6" }} />
          </Box>
        </HtmlTooltipDisabled>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        // disableScrollLock
        onClose={(event, reason) => {
          // Nested menu is portaled, so ignore backdrop close while it is open
          if (reason === "backdropClick" && nestedMenuOpen === true) {
            return;
          }
          closeMenu();
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { borderRadius: "8px", mt: 0.5 },
        }}
      >
        {/* Analytics products */}
        {showProducts === true && (
          <ActionMenuRow
            enabled={isEditAccessible}
            icon={<AddShoppingCart fontSize="small" />}
            label="Analytics products"
            ariaLabelText={"Manage analytics products for " + campaign.campaign_name}
            showCheck={productsAdded === true}
            disabledTooltip={productsDisabledTooltip}
            onClick={() => {
              closeMenu();
              setCampaignInfo(campaign);
              setOpenModal((prevState) => ({ ...prevState, products: true }));
            }}
          />
        )}

        {showActivities === true && (
          <ActionMenuRow
            enabled={isEditAccessible && productsAdded}
            icon={<Discount fontSize="small" />}
            label="Marketing activities"
            ariaLabelText={"Manage marketing activities for " + campaign.campaign_name}
            showCheck={activitiesAdded === true}
            disabledTooltip={activitiesDisabledTooltip}
            onClick={() => {
              closeMenu();
              setCampaignInfo(campaign);
              fetchRequiredDetailsBeforePopup(campaign).then(() => {
                setOpenModal((prevState) => ({ ...prevState, activity_list: true }));
              });
            }}
          />
        )}

        {/* Share */}
        {showShare === true && (
          <ActionMenuRow
            enabled={isReportShareable}
            icon={<IosShare fontSize="small" />}
            label="Share"
            ariaLabelText={"Share report for " + campaign.campaign_name}
            showCheck={campaignWasShared === true && isReportShareable === true}
            disabledTooltip={shareDisabledTooltip}
            onClick={() => {
              closeMenu();
              fetchCampaign(campaign.campaign_id, "all").then(() => {
                setCampaignInfo(campaign);
                setOpenModal((prevState) => ({ ...prevState, share_report: true }));
              });
            }}
          />
        )}

        {showMenuDivider === true && <Divider sx={{ my: 0.5 }} />}

        {/* Edit */}
        {showEdit === true && (
          <ActionMenuRow
            enabled={isUserAuthorized === true && campaignIsCompleted !== true}
            icon={<Edit fontSize="small" />}
            label="Edit"
            ariaLabelText={"edit campaign for " + campaign.campaign_name}
            showCheck={false}
            disabledTooltip={editDisabledTooltip}
            onClick={() => {
              closeMenu();
              setCampaignInfo(campaign);
              setOpenModal((prevState) => ({ ...prevState, edit_campaign: true }));
            }}
          />
        )}

        {/* Internal settings — Cyreen and tenant. Hover or click opens Report Settings / Data Sanity. */}
        {showInternalSettings === true && (
          editsAcrossCompanies === true ? (
            <MenuItem
              ref={internalSettingsRef}
              aria-label={"report settings for " + campaign.campaign_name}
              onMouseEnter={(event) => {
                openNestedMenu(event.currentTarget);
              }}
              onMouseLeave={handleLeaveHoverZone}
              onClick={(event) => {
                event.preventDefault();
                openNestedMenu(event.currentTarget);
              }}
              sx={menuItemSx}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "#AF3241" }}>
                <DisplaySettings fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Internal settings"
                primaryTypographyProps={{ sx: { color: "#1E1E28", fontSize: 14 } }}
              />
              {/* Left chevron on the right, same slot as the product/share checkmarks — submenu opens to the left */}
              <ChevronLeft sx={{ color: "#9E9E9E", fontSize: 18 }} />
            </MenuItem>
          ) : (
            <ActionMenuRow
              enabled={false}
              icon={<DisplaySettings fontSize="small" />}
              label="Internal settings"
              ariaLabelText={"report settings for " + campaign.campaign_name}
              showCheck={false}
              disabledTooltip={NO_ACCESS_TOOLTIP}
            />
          )
        )}
      </Menu>

      {/* Popover (not a second Menu modal) sits to the left; parent Action menu stays open */}
      <Popover
        anchorEl={nestedAnchor}
        open={nestedMenuOpen}
        onClose={closeNestedMenu}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        hideBackdrop
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ pointerEvents: "none" }}
        PaperProps={{
          ref: nestedPaperRef,
          onMouseEnter: cancelNestedClose,
          onMouseLeave: handleLeaveHoverZone,
          sx: { borderRadius: "8px", minWidth: 180, pointerEvents: "auto", ml: "-4px" },
        }}
      >
        <MenuList sx={{ py: 0.5 }}>
          <MenuItem
            disabled={canViewReportSettings === false}
            onClick={() => {
              closeMenu();
              if (typeof onInternalSettingSelect === "function") {
                onInternalSettingSelect("Report Settings", campaign);
              }
            }}
            sx={menuItemSx}
          >
            <ListItemText primary="Report Settings" primaryTypographyProps={{ sx: { fontSize: 14 } }} />
          </MenuItem>
          {showInternalNotes === true && (
            <MenuItem
              onClick={() => {
                closeMenu();
                if (typeof onInternalSettingSelect === "function") {
                  onInternalSettingSelect("Internal Notes", campaign);
                }
              }}
              sx={menuItemSx}
            >
              <ListItemText primary="Internal Notes" primaryTypographyProps={{ sx: { fontSize: 14 } }} />
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              closeMenu();
              if (typeof onInternalSettingSelect === "function") {
                onInternalSettingSelect("Data Sanity", campaign);
              }
            }}
            sx={menuItemSx}
          >
            <ListItemText primary="Data Sanity" primaryTypographyProps={{ sx: { fontSize: 14 } }} />
          </MenuItem>
        </MenuList>
      </Popover>
    </ListingCellCenter>
  );
};

export default CampaignActions;
